<template>
  <section
    v-if="shouldShowComponent"
    v-observe-visibility="{
      callback: visibilityChanged,
      once: true,
      intersection: { threshold: 0.5 }
    }"
    data-nba-main-section
    :class="['outer-wrapper', nbaViewModel.backgroundColorClass]"
  >
    <div class="container align-center row nba-wrapper" data-nba-main-wrapper>
      <div
        class="columns small-12 medium-5 nba-image"
        :style="nbaImageStyle"
        :alt="nbaViewModel.nbaElement.image?.alt"
        data-nba-main-image
      >
        <img
          :src="retypeImageSrcAndAlt(nbaViewModel.nbaElement.image?.url)"
          :alt="retypeImageSrcAndAlt(nbaViewModel.nbaElement.image?.alt)"
          class="columns small-12 show-for-small-only nba-image-mobile"
        />
      </div>
      <div class="columns small-12 medium-7 nba-right">
        <h2
          v-if="nbaViewModel.nbaElement.title !== ''"
          data-nba-main-title
          v-html="nbaViewModel.nbaElement.title"
        />
        <p
          v-if="nbaViewModel.nbaElement.body !== ''"
          class="nba-content"
          data-nba-main-content
          v-html="nbaViewModel.nbaElement.body"
        />
        <a
          v-if="nbaViewModel.nbaElement.callToActionLink !== null"
          :href="nbaViewModel.nbaElement.callToActionLink.url"
          data-nba-main-cta
          @click="dispatchCtaClickTrackingEvent"
          v-html="nbaViewModel.nbaElement.callToActionLink.text"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import NbaCallToAction from "./interfaces/NbaCallToAction";
import RequestService from "@/common/requestService";
import Utility from "@/common/UtilityFunctions";
import Vue from "vue";
import { ObserveVisibility } from 'vue-observe-visibility';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'NbaMain',
  props: {
    datasource: {
      type: String,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      nbaViewModel: {
        hasMarketingPermission: false,
        backgroundColorClass: '',
        nbaElement: {
          body: '',
          callToActionLink: null,
          image: null,
          link: null,
          title: '',
          trackingEventLabel: ''
        }
      } as NbaCallToAction
    };
  },
  computed: {
    shouldShowComponent(): boolean {
      return (
        this.nbaViewModel !== null &&
        this.nbaViewModel.hasMarketingPermission !== false &&
        this.nbaViewModel.nbaElement !== null &&
        this.nbaViewModel.nbaElement.body !== '' &&
        this.nbaViewModel.nbaElement.title !== '' &&
        this.nbaViewModel.nbaElement.callToActionLink?.url !== null &&
        this.nbaViewModel.nbaElement.callToActionLink?.text !== ''
      );
    },
    nbaImageStyle(): string {
      return `background-image: url("${this.nbaViewModel.nbaElement.image?.url}");`;
    }
  },
  mounted(): void {
    if (this.datasource === null) {
      return;
    }
    RequestService.get<NbaCallToAction>(Utility.constants().API.nba_get + this.datasource)
      .then((response: any) => {
        if (response.status === 200) {
          this.nbaViewModel = response.data;
        } else {
          throw new Error(
            `NBA Received non-success status code or empty data. Status: ${response.status}. Data: ${response.data}`
          );
        }
      })
      .catch((error: any) => {
        Utility.logError(`NBA component failed to retrieve NBA data with error: ${error}`, this);
      });
  },
  methods: {
    retypeImageSrcAndAlt(imageData: string | null | undefined) {
      if (imageData)
        return imageData;
    },
    dispatchLoadTrackingEvent() {
      this.$store.dispatch('Events/register', {
        event: 'nba-load',
        id: 'nba',
        vm: Utility.decodeHtml(this.nbaViewModel.nbaElement.trackingEventLabel)
      });
    },
    visibilityChanged(isVisible: boolean) {
      if (isVisible) {
        this.dispatchLoadTrackingEvent();
      }
    },
    dispatchCtaClickTrackingEvent() {
      this.$store.dispatch('Events/register', {
        event: 'nba-cta-click',
        id: 'nba',
        vm: Utility.decodeHtml(this.nbaViewModel.nbaElement.trackingEventLabel)
      });
    }
  }
});
</script>

<style lang="scss" scoped>
@import 'src/styles/all.scss';

.outer-wrapper {
  background-color: white;
  min-width: 100%;

  .nba-wrapper {
    max-width: rem-calc(913);

    .nba-image {
      min-height: rem-calc(190);
      background-position: center top;
      background-repeat: no-repeat;
      background-size: cover;

      @media #{$medium-up} {
        border-radius: 10px 0 0 10px;
      }

      .nba-image-mobile {
        width: 100%;
        opacity: 0;
      }
    }

    .nba-right {
      padding: rem-calc(25);

      @media #{$medium-up} {
        min-height: rem-calc(190);
        border-radius: 0 10px 10px 0;
      }

      h2 {
        font-size: rem-calc(22);
      }

      p {
        display: inline;
      }

      .nba-content {
        &::after {
          content: ' ';
        }
      }
    }
  }

  &.grey {
    background-color: $light-gray;

    .nba-right {
      background-color: $white;
    }
  }

  &.white {
    background-color: $white;

    .nba-right {
      background-color: $light-gray;
    }
  }
}
</style>
